import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "@/auth/useAuth";
import { IconRenderer, isIconValue } from "@/features/shared/components/IconPicker";
import Modal from "@/features/shared/components/Modal/Modal";
import TeamForm from "@/features/team/components/TeamForm";
import TeamMembersModal from "@/features/team/components/TeamMembersModal";
import TeamPagination from "@/features/team/components/TeamPagination";
import { addTeamMembers, buildTeamPayload, createTeam, deleteTeam, fetchTeam, fetchTeams, getTeamErrorMessage, removeTeamMember, updateTeam, } from "@/features/team/services/teamService";
import { fetchUsers } from "@/features/user/services/userService";
import { FiEdit, FiTrash, FiUsers } from "react-icons/fi";
const defaultFilters = {};
const TeamsPage = () => {
    const { user } = useAuth();
    const canManageTeams = Boolean(user?.permissions?.includes("can_manage_team"));
    const [teams, setTeams] = useState([]);
    const [filters, setFilters] = useState({ ...defaultFilters });
    const [searchName, setSearchName] = useState("");
    const [selectedManagerId, setSelectedManagerId] = useState("");
    const [meta, setMeta] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [detailTeam, setDetailTeam] = useState(null);
    const [detailMembers, setDetailMembers] = useState([]);
    const [formLoading, setFormLoading] = useState(false);
    const [membersLoading, setMembersLoading] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState(null);
    const loadTeams = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchTeams({
                page: currentPage,
                limit: rowsPerPage,
                name: filters.name,
                manager_id: filters.manager_id,
            });
            setTeams(response.teams);
            setMeta({
                total: response.meta?.total,
                totalPages: response.meta?.totalPages ?? 1,
                limit: response.meta?.limit ?? rowsPerPage,
            });
            setError(null);
            const totalPages = response.meta?.totalPages ?? 1;
            if (currentPage > totalPages && totalPages > 0) {
                setCurrentPage(totalPages);
            }
        }
        catch (apiError) {
            setError(getTeamErrorMessage(apiError));
        }
        finally {
            setLoading(false);
        }
    }, [currentPage, rowsPerPage, filters.name, filters.manager_id]);
    useEffect(() => {
        void loadTeams();
    }, [loadTeams]);
    const loadUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const response = await fetchUsers(undefined, undefined, true);
            const allUsers = response.users ?? [];
            setAvailableUsers(allUsers);
            setUsersError(null);
        }
        catch (apiError) {
            const message = apiError instanceof Error ? apiError.message : "Impossible de charger les utilisateurs.";
            setUsersError(message);
        }
        finally {
            setUsersLoading(false);
        }
    }, []);
    useEffect(() => {
        void loadUsers();
    }, [loadUsers]);
    const availableManagers = useMemo(() => {
        const managersWithPermission = availableUsers.filter(u => u.permissions?.includes("can_lead_team"));
        if (managersWithPermission.length > 0) {
            return managersWithPermission;
        }
        return availableUsers;
    }, [availableUsers]);
    const handleFilterByName = (value) => {
        setSearchName(value);
        setFilters(prev => ({ ...prev, name: value.trim() || undefined }));
        setCurrentPage(1);
    };
    const handleFilterByManager = (value) => {
        setSelectedManagerId(value);
        setFilters(prev => ({ ...prev, manager_id: value ? Number(value) : undefined }));
        setCurrentPage(1);
    };
    const handleResetFilters = () => {
        setSearchName("");
        setSelectedManagerId("");
        setFilters({ ...defaultFilters });
        setCurrentPage(1);
    };
    const closeForm = () => {
        setIsFormOpen(false);
        setEditingTeam(null);
    };
    const handleCreateClick = () => {
        setEditingTeam(null);
        setIsFormOpen(true);
    };
    const handleEditClick = async (teamId) => {
        setFormLoading(true);
        setIsFormOpen(true);
        try {
            const team = await fetchTeam(teamId);
            setEditingTeam(team);
        }
        catch (apiError) {
            const message = getTeamErrorMessage(apiError);
            toast.error(message);
            closeForm();
        }
        finally {
            setFormLoading(false);
        }
    };
    const handleFormSubmit = async (values) => {
        try {
            const payload = buildTeamPayload(values);
            if (editingTeam) {
                await updateTeam(editingTeam.id, payload);
                toast.success("Équipe mise à jour avec succès.");
            }
            else {
                await createTeam(payload);
                toast.success("Équipe créée avec succès.");
            }
            closeForm();
            await loadTeams();
        }
        catch (apiError) {
            const message = getTeamErrorMessage(apiError);
            toast.error(message);
        }
    };
    const handleDeleteTeam = async (teamId) => {
        const confirmed = window.confirm("Supprimer cette équipe ? Cette action est définitive.");
        if (!confirmed) {
            return;
        }
        try {
            await deleteTeam(teamId);
            toast.success("Équipe supprimée avec succès.");
            await loadTeams();
        }
        catch (apiError) {
            const message = getTeamErrorMessage(apiError);
            toast.error(message);
        }
    };
    const closeMembersModal = () => {
        setIsMembersModalOpen(false);
        setDetailTeam(null);
        setDetailMembers([]);
    };
    const openMembersModal = async (teamId) => {
        setMembersLoading(true);
        setIsMembersModalOpen(true);
        try {
            const team = await fetchTeam(teamId);
            setDetailTeam(team);
            setDetailMembers(team.members ?? []);
        }
        catch (apiError) {
            const message = getTeamErrorMessage(apiError);
            toast.error(message);
            closeMembersModal();
        }
        finally {
            setMembersLoading(false);
        }
    };
    const handleAddMembers = async (memberIds) => {
        if (!detailTeam) {
            return;
        }
        try {
            const members = await addTeamMembers(detailTeam.id, memberIds);
            setDetailMembers(members);
            toast.success("Membres ajoutés avec succès.");
            await loadTeams();
        }
        catch (apiError) {
            const message = getTeamErrorMessage(apiError);
            toast.error(message);
        }
    };
    const handleRemoveMember = async (member) => {
        if (!detailTeam) {
            return;
        }
        const confirmed = window.confirm(`Retirer ${member.first_name} ${member.last_name} de l'équipe ?`);
        if (!confirmed) {
            return;
        }
        try {
            await removeTeamMember(detailTeam.id, member.id);
            setDetailMembers(prev => prev.filter(item => item.id !== member.id));
            toast.success("Membre retiré avec succès.");
            await loadTeams();
        }
        catch (apiError) {
            const message = getTeamErrorMessage(apiError);
            toast.error(message);
        }
    };
    const formatDate = (value) => {
        if (!value) {
            return "—";
        }
        try {
            return new Intl.DateTimeFormat("fr-FR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }).format(new Date(value));
        }
        catch {
            return value;
        }
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("header", { className: "flex flex-col gap-3 md:flex-row md:items-center md:justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-semibold text-gray-900", children: "\u00C9quipes" }), _jsx("p", { className: "text-sm text-gray-600", children: "G\u00E9rez les \u00E9quipes, leurs responsables et leurs membres conform\u00E9ment aux permissions attribu\u00E9es." })] }), canManageTeams && (_jsx("button", { type: "button", onClick: handleCreateClick, className: "rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700", children: "+ Nouvelle \u00E9quipe" }))] }), _jsxs("section", { className: "rounded border border-gray-200 bg-white p-4 shadow-sm", children: [_jsx("h2", { className: "text-sm font-medium text-gray-900", children: "Filtres" }), _jsxs("div", { className: "mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("label", { htmlFor: "filter-name", className: "text-xs font-medium uppercase text-gray-500", children: "Nom" }), _jsx("input", { id: "filter-name", type: "search", value: searchName, onChange: event => handleFilterByName(event.target.value), className: "w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none", placeholder: "Rechercher une \u00E9quipe" })] }), _jsxs("div", { className: "space-y-1", children: [_jsx("label", { htmlFor: "filter-manager", className: "text-xs font-medium uppercase text-gray-500", children: "Responsable" }), _jsxs("select", { id: "filter-manager", value: selectedManagerId, onChange: event => handleFilterByManager(event.target.value), className: "w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none", children: [_jsx("option", { value: "", children: "Tous" }), availableManagers.map(manager => (_jsxs("option", { value: manager.id, children: [manager.first_name, " ", manager.last_name] }, manager.id)))] })] }), _jsx("div", { className: "flex items-end", children: _jsx("button", { type: "button", onClick: handleResetFilters, className: "rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100", children: "R\u00E9initialiser" }) })] }), usersLoading && (_jsx("p", { className: "mt-3 text-xs text-gray-500", children: "Chargement de la liste des utilisateurs\u2026" })), usersError && (_jsxs("div", { className: "mt-3 rounded border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800", children: [usersError, _jsx("button", { type: "button", onClick: () => void loadUsers(), className: "ml-3 inline-flex items-center rounded border border-yellow-400 px-2 py-1 text-xs text-yellow-700 hover:bg-yellow-100", children: "R\u00E9essayer" })] }))] }), _jsxs("section", { className: "rounded border border-gray-200 bg-white shadow-sm p-2", children: [_jsxs("header", { className: "flex items-center justify-between border-b border-gray-200 px-4 py-3", children: [_jsx("h2", { className: "text-lg font-medium text-gray-900", children: "Liste des \u00E9quipes" }), _jsx("span", { className: "text-xs text-gray-500", children: loading ? "Chargement…" : `${teams.length} résultat(s)` })] }), error ? (_jsxs("div", { className: "p-6 text-sm text-red-600", children: [_jsx("div", { className: "font-medium", children: "Une erreur est survenue." }), _jsx("p", { children: error }), _jsx("button", { type: "button", onClick: () => void loadTeams(), className: "mt-3 rounded border border-red-200 px-3 py-1 text-sm text-red-600 hover:bg-red-50", children: "R\u00E9essayer" })] })) : teams.length === 0 ? (_jsx("div", { className: "p-6 text-sm text-gray-500", children: loading ? "Chargement des équipes…" : "Aucune équipe ne correspond aux filtres." })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "min-w-full divide-y divide-gray-200", children: [_jsx("thead", { className: "bg-gray-50", children: _jsxs("tr", { children: [_jsx("th", { className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500", children: "\u00C9quipe" }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500", children: "Responsable" }), _jsx("th", { className: "px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500", children: "Membres" }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500", children: "Cr\u00E9\u00E9e le" }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500", children: "Mise \u00E0 jour le" }), _jsx("th", { className: "px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500", children: "Actions" })] }) }), _jsx("tbody", { className: "divide-y divide-gray-200 bg-white", children: teams.map(team => (_jsxs("tr", { children: [_jsx("td", { className: "px-4 py-3 text-sm text-gray-900", children: _jsxs("div", { className: "flex items-center gap-3", children: [team.icon && isIconValue(team.icon) ? (_jsx("span", { className: "flex h-9 w-9 items-center justify-center rounded-full bg-gray-100", style: { color: team.color ?? undefined }, "aria-hidden": true, children: _jsx(IconRenderer, { value: team.icon, size: 20 }) })) : (_jsx("span", { className: "inline-flex h-3 w-3 rounded-full", style: { backgroundColor: team.color ?? undefined }, "aria-hidden": true })), _jsxs("div", { children: [_jsx("div", { className: "font-medium", children: team.name }), team.icon && (_jsxs("div", { className: "text-xs text-gray-500", children: ["Ic\u00F4ne : ", team.icon] }))] })] }) }), _jsx("td", { className: "px-4 py-3 text-sm text-gray-500", children: team.manager ? (_jsxs(_Fragment, { children: [team.manager.first_name, " ", team.manager.last_name, _jsx("div", { className: "text-xs text-gray-400", children: team.manager.email })] })) : (_jsx("span", { className: "text-xs text-gray-400", children: "Aucun" })) }), _jsx("td", { className: "px-4 py-3 text-center text-sm text-gray-900", children: team.member_count ?? team.members?.length ?? 0 }), _jsx("td", { className: "px-4 py-3 text-sm text-gray-500", children: formatDate(team.created_at) }), _jsx("td", { className: "px-4 py-3 text-sm text-gray-500", children: formatDate(team.updated_at) }), _jsx("td", { className: "px-4 py-3 text-right text-sm", children: _jsxs("div", { className: "flex flex-wrap justify-end gap-3", children: [_jsx("button", { type: "button", onClick: () => void openMembersModal(team.id), className: "text-gray-600 transition hover:text-gray-800", "aria-label": "Voir les membres", title: "Voir les membres", children: _jsx(FiUsers, { size: 18 }) }), canManageTeams && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: () => void handleEditClick(team.id), className: "text-blue-600 transition hover:text-blue-800", "aria-label": "Modifier", title: "Modifier", children: _jsx(FiEdit, { size: 18 }) }), _jsx("button", { type: "button", onClick: () => void handleDeleteTeam(team.id), className: "text-red-600 transition hover:text-red-800", "aria-label": "Supprimer", title: "Supprimer", children: _jsx(FiTrash, { size: 18 }) })] }))] }) })] }, team.id))) })] }) })), _jsx(TeamPagination, { currentPage: currentPage, totalPages: meta.totalPages ?? 1, limit: rowsPerPage, totalItems: meta.total, onPageChange: page => setCurrentPage(page), onLimitChange: limit => {
                            setRowsPerPage(limit);
                            setCurrentPage(1);
                        } })] }), isFormOpen && (_jsx(Modal, { onClose: closeForm, children: formLoading ? (_jsx("div", { className: "w-80 p-4 text-center text-sm text-gray-600", children: "Chargement de l'\u00E9quipe\u2026" })) : (_jsx(TeamForm, { initialData: editingTeam ?? undefined, availableMembers: availableUsers, availableManagers: availableManagers, onSubmit: handleFormSubmit, onCancel: closeForm })) })), isMembersModalOpen && (_jsx(Modal, { onClose: closeMembersModal, children: _jsx(TeamMembersModal, { team: detailTeam, members: detailMembers, availableUsers: availableUsers, canManage: canManageTeams, loading: membersLoading, onAddMembers: handleAddMembers, onRemoveMember: handleRemoveMember, onClose: closeMembersModal }) }))] }));
};
export default TeamsPage;
